import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Row, Col, Image } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import Loader from '../components/Loader'
import Message from '../components/Message'
import { createBlogPost, updateBlogPost, getBlogPost } from '../actions/blogActions'
import { BLOG_CREATE_RESET, BLOG_UPDATE_RESET } from '../constants/blogConstants'

const CATEGORY_CHOICES = [
    { value: 'golf_news',        label: 'Golf News' },
    { value: 'club_update',      label: 'Club Update' },
    { value: 'tournament_recap', label: 'Tournament Recap' },
    { value: 'general',          label: 'Local' },
]

const isMp4 = (url) => url && url.toLowerCase().endsWith('.mp4')

function BlogEditScreen() {
    const { id } = useParams()
    const isEditing = Boolean(id)
    const dispatch  = useDispatch()
    const navigate  = useNavigate()

    const [title,          setTitle]          = useState('')
    const [category,       setCategory]       = useState('general')
    const [excerpt,        setExcerpt]        = useState('')
    const [content,        setContent]        = useState('')
    const [isPublished,    setIsPublished]    = useState(true)
    const [imageDesktop,   setImageDesktop]   = useState(null)
    const [imageMobile,    setImageMobile]    = useState(null)
    const [previewDesktop, setPreviewDesktop] = useState(null)
    const [previewMobile,  setPreviewMobile]  = useState(null)
    const [mobileMimeType, setMobileMimeType] = useState('')

    const { userInfo } = useSelector(state => state.userLogin)

    const blogCreate = useSelector(state => state.blogCreate)
    const { loading: createLoading, error: createError, success: createSuccess } = blogCreate

    const blogUpdate = useSelector(state => state.blogUpdate)
    const { loading: updateLoading, error: updateError, success: updateSuccess } = blogUpdate

    const blogDetails = useSelector(state => state.blogDetails)
    const { loading: detailsLoading, error: detailsError, post } = blogDetails

    const isAllowed = userInfo && (userInfo.isAdmin || userInfo.roles?.includes('journalist'))

    const populatedRef = useRef(false)

    // Effect 1: fetch the post once on mount (no post in deps)
    useEffect(() => {
        if (!isAllowed) { navigate('/'); return }
        if (isEditing) {
            dispatch(getBlogPost(id))
        }
    }, [id, isEditing, isAllowed, navigate, dispatch])

    // Effect 2: populate form fields once when post data arrives
    useEffect(() => {
        if (post && post.id === Number(id) && !populatedRef.current) {
            populatedRef.current = true
            setTitle(post.title || '')
            setCategory(post.category || 'general')
            setExcerpt(post.excerpt || '')
            setContent(post.content || '')
            setIsPublished(post.is_published !== undefined ? post.is_published : true)
            if (post.image_desktop_url) setPreviewDesktop(post.image_desktop_url)
            if (post.image_mobile_url) {
                setPreviewMobile(post.image_mobile_url)
                setMobileMimeType(isMp4(post.image_mobile_url) ? 'video/mp4' : 'image')
            }
        }
    }, [post, id])

    useEffect(() => {
        if (createSuccess) { dispatch({ type: BLOG_CREATE_RESET }); navigate('/news') }
    }, [createSuccess, dispatch, navigate])

    useEffect(() => {
        if (updateSuccess) { dispatch({ type: BLOG_UPDATE_RESET }); navigate('/news') }
    }, [updateSuccess, dispatch, navigate])

    const handleFileChange = (e, type) => {
        const file = e.target.files[0]
        if (!file) return
        const url = URL.createObjectURL(file)
        if (type === 'desktop') {
            setImageDesktop(file)
            setPreviewDesktop(url)
        } else {
            setImageMobile(file)
            setPreviewMobile(url)
            setMobileMimeType(file.type)
        }
    }

    const submitHandler = (e) => {
        e.preventDefault()
        const postData = { title, category, excerpt, content, is_published: isPublished }
        if (imageDesktop) postData.image_desktop = imageDesktop
        if (imageMobile)  postData.image_mobile  = imageMobile
        if (isEditing) {
            dispatch(updateBlogPost(id, postData))
        } else {
            dispatch(createBlogPost(postData))
        }
    }

    const loading = createLoading || updateLoading || detailsLoading
    const error   = createError   || updateError   || detailsError

    return (
        <div style={{ maxWidth: '780px', margin: '0 auto', paddingTop: '1.5rem' }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '12px' }}>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate('/news')}>
                    <i className="fa-solid fa-arrow-left me-1"></i> Back
                </Button>
                <h4 className="mb-0" style={{ fontWeight: '800', color: 'var(--bnb-navy)' }}>
                    {isEditing ? 'Edit Post' : 'Write New Post'}
                </h4>
            </div>

            {loading && <Loader />}
            {error && <Message variant="danger">{error}</Message>}

            <div className="bnb-card p-4">
                <Form onSubmit={submitHandler}>

                    <Form.Group className="mb-3" controlId="title">
                        <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>Title</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Post title"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            required
                        />
                    </Form.Group>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="category">
                                <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>Category</Form.Label>
                                <Form.Select value={category} onChange={e => setCategory(e.target.value)}>
                                    {CATEGORY_CHOICES.map(c => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-end mb-3">
                            <Form.Check
                                type="checkbox"
                                id="isPublished"
                                label="Published"
                                checked={isPublished}
                                onChange={e => setIsPublished(e.target.checked)}
                                style={{ fontWeight: '600', fontSize: '0.875rem' }}
                            />
                        </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="excerpt">
                        <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>
                            Excerpt <span style={{ fontWeight: '400', color: 'var(--bnb-gray)' }}>(short summary)</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Brief summary shown in listings..."
                            value={excerpt}
                            onChange={e => setExcerpt(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="content">
                        <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>Content</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={12}
                            placeholder="Write your article here..."
                            value={content}
                            onChange={e => setContent(e.target.value)}
                            required
                            style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                        />
                    </Form.Group>

                    {/* ── Images ── */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <Form.Group controlId="imageDesktop">
                                <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>Desktop Image</Form.Label>
                                {previewDesktop && (
                                    <div className="mb-2">
                                        <Image
                                            src={previewDesktop}
                                            alt="desktop preview"
                                            fluid
                                            style={{ maxHeight: '130px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--bnb-border)' }}
                                        />
                                    </div>
                                )}
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={e => handleFileChange(e, 'desktop')}
                                />
                                <Form.Text className="text-muted">Full-size — used for desktop cards</Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="imageMobile">
                                <Form.Label style={{ fontWeight: '700', fontSize: '0.85rem' }}>Mobile Image</Form.Label>
                                {previewMobile && (
                                    <div className="mb-2">
                                        {mobileMimeType === 'video/mp4' ? (
                                            <video src={previewMobile} style={{ maxHeight: '130px', maxWidth: '100%', borderRadius: '4px' }} controls muted />
                                        ) : (
                                            <Image
                                                src={previewMobile}
                                                alt="mobile preview"
                                                fluid
                                                style={{ maxHeight: '130px', objectFit: 'contain', borderRadius: '4px', border: '1px solid var(--bnb-border)' }}
                                            />
                                        )}
                                    </div>
                                )}
                                <Form.Control
                                    type="file"
                                    accept="image/*,video/mp4,.gif"
                                    onChange={e => handleFileChange(e, 'mobile')}
                                />
                                <Form.Text className="text-muted">Supports images, GIF, or MP4</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="d-flex gap-2">
                        <Button
                            type="submit"
                            disabled={loading}
                            style={{ background: 'var(--bnb-navy)', border: 'none', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.05em', padding: '0.5rem 1.5rem' }}
                        >
                            {loading ? 'Saving...' : isEditing ? 'Update Post' : 'Publish Post'}
                        </Button>
                        <Button variant="outline-secondary" onClick={() => navigate('/news')}>
                            Cancel
                        </Button>
                    </div>

                </Form>
            </div>
        </div>
    )
}

export default BlogEditScreen